import { TextField, useTheme } from '@mui/material';
import { FC, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs, { Dayjs } from 'dayjs';
import FieldGridContainer from '../../fieldGridContainer';

type DateRange = [Date | null, Date | null];

export type ControlledDateRangePickerProps = {
  control?: any;
  isGridItem?: boolean;
  label?: string;
  id: string;
  name: string;
  errorMessage?: string;
  helperText?: string;
  dataTestId?: string;
  placeholder?: string;
  value?: [Dayjs | null, Dayjs | null];
  onChange?: (fromDate: Dayjs | null, toDate: Dayjs | null) => void;
  isClearable?: boolean;
};

const ControlledDateRangePicker: FC<ControlledDateRangePickerProps> = (
  props: ControlledDateRangePickerProps
) => {
  const theme = useTheme();
  const {
    control,
    isGridItem = true,
    label,
    id,
    name,
    helperText = '',
    errorMessage,
    dataTestId = 'date-range-picker-container',
    placeholder,
    value,
    onChange,
    isClearable = true,
    ...otherProps
  } = props;

  useEffect(() => {
    const headerBackgroundColor = theme.palette.primary.light;
    const navigationIconColor =
      theme.palette.getContrastText(headerBackgroundColor);
    const navigationHoverBackgroundColor =
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.16)'
        : 'rgba(0, 0, 0, 0.08)';

    const style = document.createElement('style');
    style.textContent = `
      /* Above sticky table headers (e.g. PaginatedGrid uses z-index: 2); library default is z-index: 1 */
      .react-datepicker-popper {
        z-index: ${theme.zIndex.modal} !important;
      }
      .react-datepicker {
        font-family: ${theme.typography.fontFamily};
        border-color: ${theme.palette.divider};
        border-radius: ${theme.shape.borderRadius}px;
        box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
      }
      .react-datepicker__header {
        background-color: ${headerBackgroundColor};
        border-bottom-color: ${theme.palette.divider};
        border-radius: ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0;
      }
      .react-datepicker__current-month,
      .react-datepicker__day-name {
        color: ${theme.palette.primary.contrastText};
        font-weight: 600;
      }
      .react-datepicker__day {
        color: ${theme.palette.text.primary};
        border-radius: 8px;
        min-height: 44px;
        min-width: 44px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      @media (min-width: 600px) {
        .react-datepicker__day {
          min-height: 36px;
          min-width: 36px;
        }
      }
      .react-datepicker__day:not(.react-datepicker__day--disabled):not(.react-datepicker__day--outside-month):hover {
        background-color: ${theme.palette.primary.light};
        color: ${theme.palette.primary.contrastText};
        border-radius: 8px;
      }
      .react-datepicker__day--disabled,
      .react-datepicker__day--outside-month {
        color: ${theme.palette.text.disabled};
      }
      .react-datepicker__day--disabled:hover,
      .react-datepicker__day--outside-month:hover {
        background-color: transparent;
        color: ${theme.palette.text.disabled};
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--in-selecting-range,
      .react-datepicker__day--in-range {
        background-color: ${theme.palette.primary.main};
        color: ${theme.palette.primary.contrastText};
        font-weight: 600;
      }
      .react-datepicker__day--selected:hover,
      .react-datepicker__day--in-selecting-range:hover,
      .react-datepicker__day--in-range:hover {
        background-color: ${theme.palette.primary.light};
        color: ${theme.palette.primary.contrastText};
        font-weight: 600;
      }
      .react-datepicker__day--range-start,
      .react-datepicker__day--range-end {
        background-color: ${theme.palette.primary.light};
        color: ${theme.palette.primary.contrastText};
        font-weight: 600;
      }
      .react-datepicker__day--today {
        font-weight: 600;
        border: 1px solid ${theme.palette.primary.main};
      }
      .react-datepicker__day--keyboard-selected {
        background-color: ${theme.palette.primary.dark};
        color: ${theme.palette.primary.contrastText};
      }
      .react-datepicker__navigation {
        min-height: 44px;
        min-width: 44px;
      }
      @media (min-width: 600px) {
        .react-datepicker__navigation {
          min-height: 32px;
          min-width: 32px;
        }
      }
      .react-datepicker__navigation-icon::before {
        border-color: ${navigationIconColor};
      }
      .react-datepicker__navigation:hover {
        background-color: ${navigationHoverBackgroundColor};
        border-radius: 8px;
      }
      .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before,
      .react-datepicker__navigation:focus-visible .react-datepicker__navigation-icon::before {
        border-color: ${navigationIconColor};
      }
      .react-datepicker__triangle {
        border-bottom-color: ${theme.palette.primary.light} !important;
      }
      .react-datepicker__triangle::before {
        border-bottom-color: ${theme.palette.divider} !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [theme]);

  const convertDayjsToDateRange = (
    dayjsRange: [Dayjs | null, Dayjs | null] | undefined
  ): DateRange => {
    if (!dayjsRange) return [null, null];
    return [
      dayjsRange[0] ? dayjsRange[0].toDate() : null,
      dayjsRange[1] ? dayjsRange[1].toDate() : null,
    ];
  };

  const handleDateRangeChange = (dates: DateRange) => {
    const [start, end] = dates;
    const dayjsRange: [Dayjs | null, Dayjs | null] = [
      start ? dayjs(start) : null,
      end ? dayjs(end) : null,
    ];
    onChange?.(dayjsRange[0], dayjsRange[1]);
    return dayjsRange;
  };

  const getDateRangePicker = () => {
    if (control) {
      return (
        <Controller
          name={name ?? ''}
          control={control}
          render={({ field }) => {
            const {
              onChange: fieldOnChange,
              value: fieldValue,
              ...fieldProps
            } = field;
            const dateRangeValue = convertDayjsToDateRange(
              fieldValue as [Dayjs | null, Dayjs | null] | undefined
            );
            const startOnly =
              dateRangeValue[0] !== null && dateRangeValue[1] === null;
            const minDate = startOnly ? dateRangeValue[0] : undefined;

            return (
              <DatePicker
                {...fieldProps}
                {...otherProps}
                selectsRange
                startDate={dateRangeValue[0]}
                endDate={dateRangeValue[1]}
                minDate={minDate ? minDate : undefined}
                onChange={(dates) => {
                  const dayjsRange = handleDateRangeChange(dates);
                  fieldOnChange(dayjsRange);
                }}
                isClearable={isClearable}
                customInput={
                  <TextField
                    size="small"
                    label={label}
                    placeholder={placeholder}
                    helperText={errorMessage ?? helperText}
                    error={!!errorMessage}
                    fullWidth
                    id={id}
                    data-testid={dataTestId}
                    sx={{
                      minWidth: {
                        xs: '100%',
                        sm: '240px',
                      },
                      '& .MuiInputBase-root': {
                        minHeight: {
                          xs: '44px',
                          sm: '40px',
                        },
                        fontSize: {
                          xs: '16px', // Prevents zoom on iOS
                          sm: '14px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: {
                          xs: '0.875rem',
                          sm: '1rem',
                        },
                      },
                    }}
                  />
                }
                data-testid={dataTestId}
              />
            );
          }}
        />
      );
    }

    // Uncontrolled mode
    const dateRangeValue = convertDayjsToDateRange(value);
    const startOnly = dateRangeValue[0] !== null && dateRangeValue[1] === null;
    const minDate = startOnly ? dateRangeValue[0] : undefined;

    return (
      <DatePicker
        {...otherProps}
        selectsRange
        startDate={dateRangeValue[0]}
        endDate={dateRangeValue[1]}
        minDate={minDate ? minDate : undefined}
        onChange={handleDateRangeChange}
        isClearable={isClearable}
        customInput={
          <TextField
            size="small"
            label={label}
            placeholder={placeholder}
            helperText={errorMessage ?? helperText}
            error={!!errorMessage}
            fullWidth
            id={id}
            data-testid={dataTestId}
            sx={{ minWidth: '240px' }}
          />
        }
        data-testid={dataTestId}
      />
    );
  };

  if (isGridItem) {
    return (
      <FieldGridContainer dataTestId={dataTestId}>
        {getDateRangePicker()}
      </FieldGridContainer>
    );
  }

  return <>{getDateRangePicker()}</>;
};

export default ControlledDateRangePicker;
